import { FiltersType } from '@/api/types';
import { client } from '@/client';
import {
  Benchmark,
  ListBenchmarksResponse,
  SaveBenchmarkRequest,
  UpdateBenchmarkRequest,
} from '@/client/campaigns';
import {
  GenericForbiddenApiError,
  SaveBenchmarkMutation,
  UpdateBenchmarkMutation,
} from '@/hooks/query';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const queryKey = 'benchmarks';

export const useBenchmarks = (
  params: UseQueryOptions & {
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  } = {},
): UseQueryResult<ListBenchmarksResponse> & {
  benchmarks: ListBenchmarksResponse | undefined;
} => {
  const {
    take = undefined,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [queryKey, take, skip, JSON.stringify(sort), JSON.stringify(filters)],
    () => client.benchmarks.getBenchmarks(params),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    benchmarks: data,
    ...(rest as UseQueryResult<ListBenchmarksResponse>),
  };
};

export const useSaveBenchmark = (): {
  create: (payload: SaveBenchmarkRequest) => Promise<Benchmark>;
} & SaveBenchmarkMutation => {
  const { mutateAsync, ...rest } = useMutation<
    Benchmark,
    GenericForbiddenApiError,
    SaveBenchmarkRequest
  >('createBenchmark', (payload: SaveBenchmarkRequest) =>
    client.benchmarks.saveBenchmark(payload),
  );

  return { create: mutateAsync, ...(rest as any) };
};

export const useUpdateBenchmark = (): {
  update: (payload: UpdateBenchmarkRequest) => Promise<Benchmark>;
} & UpdateBenchmarkMutation => {
  const { mutateAsync, ...rest } = useMutation<
    Benchmark,
    GenericForbiddenApiError,
    UpdateBenchmarkRequest
  >('updateBenchmark', (payload: UpdateBenchmarkRequest) =>
    client.benchmarks.updateBenchmark(payload),
  );

  return { update: mutateAsync, ...(rest as any) };
};
